import type { ThemeConfig } from 'antd'
import { theme as antTheme } from 'antd'

import {
  colors,
  radius,
  shadows,
  studioColors,
  studioFont,
  studioRadius,
  studioShadows,
  typography,
} from './design-system/tokens'

export type ThemeMode = 'light' | 'dark'
/** «الكلاسيكي» (كحلي وذهبي) أو «ستوديو» (أزرق حيّ على بياض — الثيم الثاني) */
export type ThemeStyle = 'classic' | 'studio'

/**
 * Build the Ant Design ConfigProvider theme from SYSTEM AUFQ tokens.
 *
 * IMPORTANT: do NOT hard-code colours here — extend `src/design-system/tokens.ts`
 * instead. This file's only job is to wire those tokens into Ant's API.
 */
export const buildTheme = (
  mode: ThemeMode,
  lang: 'ar' | 'en',
  style: ThemeStyle = 'classic',
): ThemeConfig => {
  if (style === 'studio') return buildStudioTheme(mode)
  const isDark = mode === 'dark'
  const c = isDark ? colors.dark : colors.light
  const sh = isDark ? shadows.dark : shadows.light

  return {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    cssVar: {},
    token: {
      colorPrimary: c.accent,
      colorSuccess: c.success,
      colorWarning: c.warn,
      colorError: c.danger,
      colorInfo: c.info,

      colorBgLayout: c.bg,
      colorBgContainer: c.bgCard,
      colorBgElevated: c.bgElevated,
      colorBorder: c.line,
      colorBorderSecondary: c.line2,
      colorFillSecondary: c.bgSubtle,
      colorFillTertiary: c.bgSubtle,
      colorTextBase: c.ink,
      colorText: c.ink,
      colorTextSecondary: c.ink2,
      colorTextTertiary: c.ink3,

      borderRadius: radius.md,
      borderRadiusLG: radius.lg,
      borderRadiusSM: radius.sm,
      fontSize: typography.fontSize,
      fontSizeHeading1: typography.fontSizeHeading1,
      fontSizeHeading2: typography.fontSizeHeading2,
      fontSizeHeading3: typography.fontSizeHeading3,
      fontSizeHeading4: typography.fontSizeHeading4,
      wireframe: false,
      motionDurationMid: '0.2s',

      boxShadow: sh.sm,
      boxShadowSecondary: sh.md,

      fontFamily: lang === 'ar' ? typography.fontFamilyAr : typography.fontFamilyEn,
    },
    components: {
      Layout: {
        headerBg: c.bgCard,
        siderBg: c.bgCard,
        bodyBg: c.bg,
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: c.accentSoft,
        itemSelectedColor: c.accentInk,
        itemHoverBg: c.bgSubtle,
        subMenuItemBg: 'transparent',
        iconSize: 17,
        fontSize: 13.5,
      },
      Card: {
        borderRadiusLG: radius.md,
        headerBg: 'transparent',
      },
      Button: {
        borderRadius: radius.sm,
        borderRadiusLG: radius.md,
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 30,
        fontWeight: 700,
        primaryShadow: `0 2px 8px ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(14,32,41,0.18)'}`,
        defaultBorderColor: c.line,
        paddingInline: 18,
      },
      Input: {
        borderRadius: radius.sm,
        controlHeight: 36,
        activeBorderColor: c.accent,
        hoverBorderColor: c.accent,
      },
      Select: {
        borderRadius: radius.sm,
        controlHeight: 36,
      },
      Table: {
        borderRadiusLG: radius.md,
        headerBg: c.bgSubtle,
        headerColor: c.ink3,
        rowHoverBg: c.bgSubtle,
        cellPaddingBlock: 13,
        cellPaddingInline: 16,
      },
      Tabs: {
        cardBg: c.bgCard,
        itemActiveColor: c.accent,
        itemSelectedColor: c.accent,
        inkBarColor: c.accent,
      },
      Breadcrumb: {
        linkColor: c.ink3,
        lastItemColor: c.ink,
        separatorColor: c.line,
        fontSize: 13,
      },
      Statistic: {
        contentFontSize: 26,
      },
      Typography: {
        titleMarginBottom: '0.4em',
      },
    },
  }
}

/**
 * «ستوديو»: الثيم الثاني — أزرق حيّ (#2F6BFF) على بياض ناصع ورمادي بارد، زوايا ١٤–٢٢،
 * عناصر تحكّم أعلى (٤٠) بحلقة تركيز ناعمة، وزر أساسي بتوهّج. الشكل الدقيق للغلاف
 * (القائمة الجانبية، الترويسة، البطاقات، الجداول) في `src/styles/studio.css`.
 */
const buildStudioTheme = (mode: ThemeMode): ThemeConfig => {
  const isDark = mode === 'dark'
  const c = isDark ? studioColors.dark : studioColors.light
  const sh = isDark ? studioShadows.dark : studioShadows.light
  const r = studioRadius

  return {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    cssVar: {},
    token: {
      colorPrimary: c.accent,
      colorLink: c.accent,
      colorSuccess: c.success,
      colorWarning: c.warn,
      colorError: c.danger,
      colorInfo: c.info,

      colorBgLayout: c.bg,
      colorBgContainer: c.bgCard,
      colorBgElevated: c.bgElevated,
      colorBorder: c.line,
      colorBorderSecondary: c.line2,
      colorFillSecondary: c.bgSubtle,
      colorFillTertiary: c.bgSubtle,
      colorFillQuaternary: c.bgSubtle,
      colorTextBase: c.ink,
      colorText: c.ink,
      colorTextSecondary: c.ink2,
      colorTextTertiary: c.ink3,
      colorTextQuaternary: c.ink3,
      colorSplit: c.line2,

      borderRadius: r.sm + 2,
      borderRadiusLG: r.lg,
      borderRadiusSM: r.sm - 2,
      borderRadiusXS: 6,
      controlHeight: 36,
      controlHeightLG: 42,
      controlHeightSM: 28,
      fontSize: 13,
      fontSizeHeading1: 24,
      fontSizeHeading2: 19,
      fontSizeHeading3: 16,
      fontSizeHeading4: 14.5,
      lineWidthFocus: 3,
      wireframe: false,
      motionDurationMid: '0.22s',
      motionEaseInOut: 'cubic-bezier(0.22, 1, 0.36, 1)',

      boxShadow: sh.sm,
      boxShadowSecondary: sh.lg,
      boxShadowTertiary: sh.xs,

      fontFamily: studioFont,
    },
    components: {
      Layout: {
        headerBg: c.bgCard,
        siderBg: c.bgCard,
        bodyBg: c.bg,
        headerHeight: 64,
      },
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: c.ink2,
        itemHoverColor: c.ink,
        itemHoverBg: c.bgSubtle,
        itemSelectedBg: c.accentSoft,
        itemSelectedColor: c.accent,
        itemActiveBg: c.accentSoft,
        groupTitleColor: c.ink3,
        itemHeight: 36,
        itemBorderRadius: 10,
        subMenuItemBorderRadius: 10,
        itemMarginInline: 8,
        itemMarginBlock: 2,
        iconSize: 16,
        collapsedIconSize: 17,
        collapsedWidth: 72,
        fontSize: 13,
        // القائمة الجانبية الكحلية-الملكية (theme="dark"): أبيض شفاف والنشِط كبسولة بيضاء بأزرق
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkPopupBg: isDark ? '#223494' : '#3857D0',
        darkItemColor: 'rgba(255, 255, 255, 0.74)',
        darkItemHoverColor: '#FFFFFF',
        darkItemHoverBg: 'rgba(255, 255, 255, 0.09)',
        darkItemSelectedBg: '#FFFFFF',
        darkItemSelectedColor: c.accent === '#5B8CFF' ? '#2F6BFF' : c.accent,
        darkGroupTitleColor: 'rgba(255, 255, 255, 0.45)',
        darkDangerItemColor: '#FF8A8E',
      },
      Card: {
        borderRadiusLG: r.lg,
        headerBg: 'transparent',
        headerFontSize: 14.5,
        headerHeight: 50,
        paddingLG: 18,
      },
      Button: {
        borderRadius: r.sm + 2,
        borderRadiusLG: r.md,
        borderRadiusSM: 9,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 600,
        paddingInline: 15,
        primaryShadow: sh.glow,
        defaultShadow: 'none',
        dangerShadow: 'none',
        defaultBorderColor: c.line,
        defaultHoverBorderColor: c.accent,
        defaultHoverColor: c.accent,
      },
      Input: {
        borderRadius: r.sm + 2,
        controlHeight: 36,
        paddingInline: 12,
        activeBorderColor: c.accent,
        hoverBorderColor: isDark ? '#2C3648' : '#CBD3DF',
        activeShadow: `0 0 0 4px ${c.accentSoft}`,
      },
      InputNumber: {
        borderRadius: r.sm + 2,
        controlHeight: 36,
        activeShadow: `0 0 0 4px ${c.accentSoft}`,
      },
      Select: {
        borderRadius: r.sm + 2,
        controlHeight: 36,
        optionSelectedBg: c.accentSoft,
        optionSelectedColor: c.accent,
        optionActiveBg: c.bgSubtle,
        optionPadding: '7px 11px',
        activeOutlineColor: c.accentSoft,
      },
      DatePicker: {
        borderRadius: r.sm + 2,
        controlHeight: 36,
        activeShadow: `0 0 0 4px ${c.accentSoft}`,
        cellActiveWithRangeBg: c.accentSoft,
      },
      Table: {
        borderRadiusLG: r.lg,
        headerBg: c.bgCard,
        headerColor: c.ink3,
        headerSplitColor: 'transparent',
        headerBorderRadius: r.md,
        rowHoverBg: c.bgSubtle,
        rowSelectedBg: c.accentSoft,
        rowSelectedHoverBg: c.accentSoft,
        borderColor: c.line2,
        cellPaddingBlock: 11,
        cellPaddingInline: 14,
        footerBg: c.bgCard,
      },
      Tabs: {
        cardBg: c.bgSubtle,
        itemColor: c.ink2,
        itemHoverColor: c.ink,
        itemActiveColor: c.accent,
        itemSelectedColor: c.accent,
        inkBarColor: c.accent,
        titleFontSize: 13,
        horizontalItemPadding: '10px 4px',
        horizontalItemGutter: 24,
      },
      Segmented: {
        trackBg: c.bgSubtle,
        itemSelectedBg: c.bgCard,
        itemColor: c.ink2,
        itemHoverColor: c.ink,
        itemSelectedColor: c.ink,
        borderRadius: r.sm + 2,
        borderRadiusSM: 9,
        trackPadding: 3,
        controlHeight: 32,
      },
      Tag: {
        defaultBg: c.bgSubtle,
        defaultColor: c.ink2,
        borderRadiusSM: 999,
      },
      Modal: {
        borderRadiusLG: r.xl,
        titleFontSize: 15.5,
        headerBg: 'transparent',
        contentBg: c.bgElevated,
        footerBg: 'transparent',
      },
      Drawer: {
        colorBgElevated: c.bgElevated,
        footerPaddingBlock: 14,
        footerPaddingInline: 22,
      },
      Dropdown: {
        borderRadiusLG: r.md,
        borderRadiusSM: 9,
        paddingBlock: 9,
        controlItemBgHover: c.bgSubtle,
        controlItemBgActive: c.accentSoft,
      },
      Popover: {
        borderRadiusLG: r.lg,
      },
      Tooltip: {
        borderRadius: 9,
        colorBgSpotlight: isDark ? '#1E2636' : '#0F172A',
      },
      Badge: {
        indicatorHeight: 16,
        textFontSize: 10.5,
        textFontWeight: 700,
      },
      Switch: {
        trackHeight: 24,
        trackMinWidth: 44,
        handleSize: 20,
      },
      Progress: {
        defaultColor: c.accent,
        remainingColor: c.line2,
      },
      Breadcrumb: {
        linkColor: c.ink3,
        lastItemColor: c.ink,
        separatorColor: c.line,
        fontSize: 13,
      },
      Statistic: {
        contentFontSize: 22,
      },
      Alert: {
        borderRadiusLG: r.md,
        withDescriptionPadding: '14px 18px',
      },
      Pagination: {
        itemActiveBg: c.accent,
        borderRadius: 10,
        itemSize: 30,
      },
      Avatar: {
        containerSize: 34,
      },
      Typography: {
        titleMarginBottom: '0.4em',
      },
    },
  }
}
